'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuthGuard } from '@/hooks/useAuthGuard';
import { knowledgeBaseApi } from '../_lib/api';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Loader2, Share2 } from 'lucide-react';
import { toast } from '@/lib/toast';
import {
  DocumentEditor,
  useEditorStore,
  type DocumentEditorRef,
} from '@/features/document-editor';

export default function KnowledgeBaseEditorPage() {
  const { t } = useTranslation();
  const { isReady } = useAuthGuard();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [articleId, setArticleId] = useState<string | null>(null);
  const editorRef = useRef<DocumentEditorRef>(null);
  const [activeHeading, setActiveHeading] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const authorName = t.knowledgeBase.editor.layout.authorName;
  const authorInitial = authorName.trim().slice(0, 1) || 'D';

  const docTitle = title.trim() || t.knowledgeBase.editor.layout.draftTitle;

  // 从 store 获取标题列表
  const { headings } = useEditorStore();

  // 标题列表转换为大纲格式
  const tocItems = headings.map((h) => ({
    id: h.id,
    label: h.text,
    level: h.level,
  }));

  useEffect(() => {
    if (tocItems.length === 0) {
      setActiveHeading(null);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveHeading(visible[0].target.id);
        }
      },
      { rootMargin: '-100px 0px -60% 0px', threshold: [0, 1] }
    );

    tocItems.forEach((heading) => {
      const element = document.getElementById(heading.id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [tocItems]);

  const handleHeadingClick = (id: string) => {
    const element = document.getElementById(id);
    if (!element) {
      return;
    }
    const top = element.getBoundingClientRect().top + window.scrollY - 60;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const handleContentChange = (json: string) => {
    setContent(json);
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error(t.knowledgeBase.editor.validation.titleRequired);
      return;
    }
    if (!editorRef.current || editorRef.current.isEmpty()) {
      toast.error(t.knowledgeBase.editor.validation.contentRequired);
      return;
    }

    try {
      setSaving(true);
      const jsonContent = JSON.stringify(editorRef.current.getJSON());

      if (!articleId) {
        // apiClient 响应拦截器已自动解包，response 直接是 KnowledgeArticle
        const response = await knowledgeBaseApi.createArticle({
          title: title.trim(),
          content: jsonContent,
        });
        setArticleId(response.id);
        toast.success(t.knowledgeBase.editor.created);
      } else {
        await knowledgeBaseApi.updateArticle(articleId, {
          title: title.trim(),
          content: jsonContent,
        });
        toast.success(t.knowledgeBase.editor.saved);
      }
    } catch (error) {
      console.error('Failed to save article:', error);
      toast.error(t.knowledgeBase.editor.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  if (!isReady) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 bg-white">
      {/* 右侧大纲栏 - 固定在右侧 */}
      <div className="relative flex min-w-0 flex-1">
        {/* 主内容区 */}
        <main className="flex min-w-0 flex-1 flex-col">
          {/* 顶部工具栏 */}
          <header className="flex h-12 flex-shrink-0 items-center justify-between border-b border-gray-100 bg-white px-4">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>{t.knowledgeBase.editor.layout.breadcrumbRoot}</span>
              <ChevronRight className="h-4 w-4 text-gray-300" />
              <span className="max-w-[200px] truncate font-medium text-gray-900">
                {docTitle}
              </span>
              <span className="text-xs text-gray-400">
                {saving ? t.knowledgeBase.editor.saving : ''}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="default"
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="h-8 gap-1.5 bg-blue-500 px-3 text-white hover:bg-blue-600"
              >
                {saving ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Share2 className="h-3.5 w-3.5" />
                )}
                {t.knowledgeBase.editor.save}
              </Button>
            </div>
          </header>

          {/* 编辑区域 */}
          <div className="flex-1 overflow-y-auto">
            <div className="mx-auto max-w-3xl px-8 py-8">
              {/* 标题输入 */}
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t.knowledgeBase.editor.fields.titlePlaceholder}
                className="mb-2 w-full border-none bg-transparent text-2xl font-semibold text-gray-900 placeholder:text-gray-300 focus:outline-none focus:ring-0"
              />

              {/* 作者信息 */}
              <div className="mb-6 flex items-center gap-2 text-sm text-gray-400">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-orange-100 text-xs font-medium text-orange-600">
                  {authorInitial}
                </div>
                <span className="text-gray-600">{authorName}</span>
                <span className="text-gray-300">|</span>
                <span>{t.common.today}{t.knowledgeBase.editor.layout.modified}</span>
              </div>

              {/* 文档编辑器 */}
              <DocumentEditor
                ref={editorRef}
                value={content}
                placeholder={t.knowledgeBase.editor.fields.contentPlaceholder}
                onChange={handleContentChange}
                minHeight="400px"
              />
            </div>
          </div>
        </main>

        {/* 右侧大纲栏 */}
        <aside
          className={`flex-shrink-0 border-l border-gray-100 bg-gray-50/50 transition-all duration-200 ${
            sidebarCollapsed ? 'w-0 overflow-hidden' : 'w-48'
          }`}
        >
          {!sidebarCollapsed && (
            <div className="h-full overflow-y-auto px-3 py-4">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-medium text-gray-500">
                  {t.knowledgeBase.editor.layout.outlineTitle}
                </span>
                <button
                  type="button"
                  onClick={() => setSidebarCollapsed(true)}
                  className="flex h-5 w-5 items-center justify-center rounded text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
              {tocItems.length > 0 ? (
                <nav className="space-y-0.5">
                  {tocItems.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => handleHeadingClick(item.id)}
                      className={`flex w-full items-center rounded px-2 py-1 text-left text-[13px] transition-colors ${
                        activeHeading === item.id
                          ? 'bg-blue-50 font-medium text-blue-600'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                      style={{ paddingLeft: item.level <= 1 ? 8 : 8 + (item.level - 1) * 12 }}
                    >
                      <span className="truncate">{item.label}</span>
                    </button>
                  ))}
                </nav>
              ) : (
                <p className="px-2 text-xs text-gray-400">
                  {t.knowledgeBase.editor.layout.outlineEmpty}
                </p>
              )}
            </div>
          )}
        </aside>

        {/* 展开按钮（侧边栏折叠时显示） */}
        {sidebarCollapsed && (
          <button
            type="button"
            onClick={() => setSidebarCollapsed(false)}
            className="absolute right-0 top-16 z-10 flex h-6 w-6 items-center justify-center rounded-l-md border border-r-0 border-gray-200 bg-white text-gray-400 shadow-sm hover:text-gray-600"
            title={t.knowledgeBase.editor.layout.outlineTitle}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
